import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Loader from 'react-loader-spinner';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Paragraph from '../Paragraph/Paragraph';
import themeColors from '../../assets/styles/themeColors';

const PDFJS_VERSION = '2.6.347';
const PDFJS_CDN = `https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/build`;

const loadPdfJs = () =>
  new Promise((resolve, reject) => {
    if (window.pdfjsLib) {
      resolve(window.pdfjsLib);
      return;
    }
    const script = document.createElement('script');
    script.src = `${PDFJS_CDN}/pdf.min.js`;
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = `${PDFJS_CDN}/pdf.worker.min.js`;
      resolve(window.pdfjsLib);
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });

const ScrollWrapper = styled.div`
  position: relative;
  width: 100%;
  max-height: min(600px, 70vh);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 0;
`;

const PdfCanvas = ({ dataUrl }) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!dataUrl) return;

    let cancelled = false;
    setLoading(true);

    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }

    const render = async () => {
      const pdfjsLib = await loadPdfJs();
      const pdf = await pdfjsLib.getDocument(dataUrl).promise;

      if (cancelled) return;
      setLoading(false);

      /* eslint-disable no-await-in-loop */
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
        if (cancelled) break;

        const page = await pdf.getPage(pageNum);
        const containerWidth = containerRef.current?.offsetWidth || 300;
        const viewport = page.getViewport({ scale: 1 });
        const scale = containerWidth / viewport.width;
        const scaledViewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;
        canvas.style.cssText =
          'width:100%;height:auto;display:block;margin-bottom:4px;';

        if (containerRef.current) {
          containerRef.current.appendChild(canvas);
        }

        await page.render({
          canvasContext: canvas.getContext('2d'),
          viewport: scaledViewport,
        }).promise;
      }
      /* eslint-enable no-await-in-loop */
    };

    render().catch(() => {
      if (!cancelled) setLoading(false);
    });

    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
    };
  }, [dataUrl]);

  return (
    <ScrollWrapper>
      {loading && (
        <LoadingOverlay>
          <Paragraph>{t('kycVerification.pdfLoading')}</Paragraph>
          <Loader
            type="TailSpin"
            color={themeColors.colorLoader}
            height={80}
            width={80}
          />
        </LoadingOverlay>
      )}
      <div ref={containerRef} />
    </ScrollWrapper>
  );
};

PdfCanvas.propTypes = {
  dataUrl: PropTypes.string,
};

export default PdfCanvas;
